import { useState } from 'react';
import { matchesScope, type Tenant, type TenantRole } from '../../../identity/api';
import { Button, Chip, Dialog, TextField } from '@nyce/admin-kit/ui';
import { client } from '../../../identity/session';
import { ScopeSelect } from './ScopeSelect';
import {
  holdsTemplate,
  ROLE_TEMPLATES,
  templateDiff,
  templateOf,
  withoutTemplate,
  withTemplate,
} from '../../../identity/roleTemplates';
import { fmtDate, sortedByName } from '../../../identity/format';
import { ConfirmButton, EmptyState, ErrorBox, ScimChip, TableScroller } from '../../../components/common';

export const tenantScopes = (t: Tenant) => t.claims.filter((c) => c.type === 'scope').map((c) => c.value);

/** True when the tenant's scope claims cover `scope` in either direction (AUTH §4.3: tenant claims are the ceiling). */
export const covered = (granted: string[], scope: string) =>
  matchesScope(granted, scope) || granted.some((g) => matchesScope([scope], g));

function RoleDialog({
  tenant,
  role,
  onSaved,
  onClose,
}: {
  tenant: Tenant;
  role?: TenantRole;
  onSaved: (r: TenantRole) => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState(role?.name ?? '');
  const [scopes, setScopes] = useState<string[]>(role?.scopes ?? []);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ceiling = tenantScopes(tenant);
  const outside = scopes.filter((s) => !covered(ceiling, s));
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = { name: name.trim(), scopes };
      const saved = role
        ? await client.tenants.roles.update(tenant.id, role.id, body)
        : await client.tenants.roles.create(tenant.id, body);
      await onSaved(saved);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={role ? `Role ${role.name}` : 'New role'}
      maxWidth={640}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void save()} loading={busy} disabled={!name.trim()}>
            {role ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <ErrorBox error={error} />
      <div className="nyk-form">
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={256}
          placeholder="clerk"
          hint="Matched case-sensitively against the user's role claim."
          required
          autoFocus
          fieldClassName="nyk-span2"
        />
        <div className="oph-claim-group nyk-span2" data-testid="role-templates">
          <span className="nyk-ui-hint">
            Templates (hub page + the service's admin scope; click again to remove)
          </span>
          <div className="nyk-chips">
            {ROLE_TEMPLATES.map((t) => {
              const held = holdsTemplate(scopes, t);
              return (
                <Chip
                  key={t.id}
                  size="sm"
                  selected={held}
                  title={`${t.hint}: ${t.scopes.join(', ')}`}
                  onClick={() => {
                    setScopes(held ? withoutTemplate(scopes, t) : withTemplate(scopes, t));
                    if (!held && !name.trim()) setName(t.name);
                  }}
                >
                  {t.label}
                </Chip>
              );
            })}
          </div>
        </div>
        <ScopeSelect
          ceiling={ceiling}
          value={scopes}
          onChange={setScopes}
          known={tenant.roles.filter((r) => r.id !== role?.id).flatMap((r) => r.scopes)}
          placeholder="service-a:items:read, auth:tenants:read…"
          hint="Granted to users with this role, capped by the tenant's scope claims."
        />
        {outside.length > 0 && (
          <div className="nyk-notice nyk-notice-warn nyk-span2" data-testid="role-outside-ceiling">
            Not covered by the tenant's claims, so not granted until the tenant gets them:{' '}
            {outside.map((s) => (
              <code key={s}>{s} </code>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
}

/**
 * `tenant_roles`: with no roles every user receives every tenant scope; with roles, a user only receives the
 * scopes of the roles they hold (identity `role` claim), and only within the tenant's scope claims.
 */
export function RolesTab({
  tenant,
  canWrite,
  onChanged,
  notify,
}: {
  tenant: Tenant;
  canWrite: boolean;
  onChanged: () => Promise<void>;
  notify: { success: (t: string) => void; error: (t: string) => void };
}) {
  const [dialog, setDialog] = useState<{ role?: TenantRole } | null>(null);
  const ceiling = tenantScopes(tenant);
  const remove = async (r: TenantRole) => {
    try {
      await client.tenants.roles.remove(tenant.id, r.id);
      await onChanged();
      notify.success(`Removed role ${r.name}`);
    } catch (e) {
      notify.error((e as Error).message);
    }
  };
  const update = async (r: TenantRole, scopes: string[]) => {
    try {
      await client.tenants.roles.update(tenant.id, r.id, { name: r.name, scopes });
      await onChanged();
      notify.success(`Updated role ${r.name} to its template`);
    } catch (e) {
      notify.error((e as Error).message);
    }
  };
  const roles = sortedByName(tenant.roles ?? [], (r) => r.name);
  return (
    <div className="nyk-card">
      <div className="nyk-card-head">
        <h2>Roles</h2>
        {canWrite && (
          <Button size="sm" onClick={() => setDialog({})}>
            New role
          </Button>
        )}
      </div>
      <p className="nyk-card-intro">
        A role maps a name from the user's <code>role</code> claim (Workserver roles, or the roles of a local
        user) to scopes. {roles.length ? 'Users without a listed role get no scope at all.' : ''}
      </p>
      {roles.length > 0 && !ceiling.length && (
        <div className="nyk-notice nyk-notice-warn">
          The tenant has no scope claims, so no role can grant anything. Add scopes under Claims first.
        </div>
      )}
      <TableScroller>
        <table className="nyk-ui-table nyk-cards" data-testid="roles-table">
          <thead>
            <tr>
              <th>Role</th>
              <th>Scopes</th>
              <th>Updated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => {
              const template = templateOf(r.name);
              const diff = template && templateDiff(r.scopes, template);
              const differs = !!diff && (diff.missing.length > 0 || diff.extra.length > 0);
              return (
                <tr key={r.id} data-role={r.id}>
                  <td className="nyk-card-title">
                    <b>{r.name}</b>
                    {r.externalId && <ScimChip externalId={r.externalId} />}
                    {ROLE_TEMPLATES.some((t) => holdsTemplate(r.scopes, t)) && (
                      <div className="nyk-ui-hint" data-testid="role-template-names">
                        {ROLE_TEMPLATES.filter((t) => holdsTemplate(r.scopes, t))
                          .map((t) => t.label)
                          .join(', ')}
                      </div>
                    )}
                    {differs && (
                      <div className="nyk-ui-hint" data-testid="role-template-diff">
                        Differs from template {template.label}:
                        {diff.missing.map((x) => (
                          <code key={`+${x}`}> +{x}</code>
                        ))}
                        {diff.extra.map((x) => (
                          <code key={`-${x}`}> −{x}</code>
                        ))}
                      </div>
                    )}
                  </td>
                  <td data-label="Scopes">
                    <div className="nyk-inline">
                      {r.scopes.map((s) => (
                        <Chip
                          key={s}
                          size="sm"
                          selected={covered(ceiling, s)}
                          title={covered(ceiling, s) ? undefined : 'Outside the tenant claims: not granted'}
                        >
                          {s}
                        </Chip>
                      ))}
                      {!r.scopes.length && <span className="nyk-ui-hint">—</span>}
                    </div>
                  </td>
                  <td data-label="Updated">{fmtDate(r.updatedAt)}</td>
                  <td className="nyk-row-actions">
                    {canWrite && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setDialog({ role: r })}>
                          Edit
                        </Button>
                        {differs && (
                          <ConfirmButton
                            size="sm"
                            variant="secondary"
                            title={`Set the scopes to template ${template.label}`}
                            message={`Update role ${r.name} to template ${template.label}?${
                              diff.missing.length ? ` Adds ${diff.missing.join(', ')}.` : ''
                            }${diff.extra.length ? ` Removes ${diff.extra.join(', ')}.` : ''}`}
                            onConfirm={() => void update(r, template.scopes)}
                          >
                            Update to template
                          </ConfirmButton>
                        )}
                        <ConfirmButton
                          size="sm"
                          variant="danger"
                          message={`Remove role ${r.name}? Users holding only this role lose every scope at their next refresh.`}
                          onConfirm={() => void remove(r)}
                        >
                          Remove
                        </ConfirmButton>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableScroller>
      {!roles.length && (
        <EmptyState
          title="No roles."
          hint="Every user of this tenant receives all of its scope claims. Add a role to differentiate users."
        />
      )}
      {dialog && (
        <RoleDialog
          tenant={tenant}
          role={dialog.role}
          onSaved={async (r) => {
            await onChanged();
            notify.success(`${dialog.role ? 'Saved' : 'Created'} role ${r.name}`);
          }}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
